/**
 * advocateSessions.js - Phase 999.102 cloud-backed implementation.
 *
 * Sessions and messages now live in the same DynamoDB table that
 * `chat_handler_v9` uses, accessed via the `/sessions` API
 * (`session_manager_v5` Lambda). localStorage is no longer the source of
 * truth - it is read once at startup so legacy entries can be migrated and
 * then cleared.
 *
 * Public exports keep their pre-999.102 names so call sites
 * (AdvocateChatPage, AdvocateChatEmbed, Sidebar) need only minimal updates.
 * Read paths are now async (Promise of an array instead of a synchronous
 * array). Callers that previously read synchronously now await.
 *
 * D-05 (CONTEXT 999.102): function names preserved.
 * D-06: one-time migration helper called from AdvocateChatPage on mount.
 */

import apiService from '../services/apiService';
import { neutraliseSessionTitle } from './sessionTitles';

// Legacy localStorage keys - kept ONLY so the migration helper can find +
// read + clear them. New writes never touch these keys.
export const ADVOCATE_SESSIONS_KEY = (userId) =>
  `afterservice_advocate_sessions_${userId}`;

export const ADVOCATE_MESSAGES_KEY = (userId, sessionId) =>
  `afterservice_advocate_msgs_${userId}_${sessionId}`;

// Marker key - set after migration completes so we never re-run on the same
// browser session.
const MIGRATION_DONE_KEY = (userId) =>
  `afterservice_advocate_migration_complete_${userId}`;

/**
 * Returns advocate sessions for a user, fetched from the cloud.
 *
 * @param {string} userId
 * @returns {Promise<Array<{ sessionId: string, title: string, titleSource: string|null, messageCount: number, lastActivity: string }>>}
 */
export async function loadAdvocateSessions(userId) {
  if (!userId || userId === 'anonymous') return [];
  try {
    const sessions = await apiService.listSessionsBySource({ source: 'advocate' });
    return (sessions || []).map((s) => {
      const lastActivity = s.lastMessageAt || s.updatedAt || s.createdAt || '';
      return {
        sessionId: s.sessionId,
        // PROD review 2026-07-30 #6 - stored titles were historically the
        // verbatim first user message. Every read passes through the
        // neutraliser so old titles stop displaying raw disclosures.
        // UX-B0: a veteran's own rename (titleSource 'user') is left as typed.
        title: neutraliseSessionTitle(s.title, lastActivity || undefined, s.titleSource),
        titleSource: s.titleSource || null,
        messageCount: s.messageCount || 0,
        lastActivity,
      };
    });
  } catch (_) {
    return [];
  }
}

// 999.90 Wave 2 - strip <safety>{...}</safety> blocks from historical
// message content. Sessions saved before the parser shipped contain raw
// blocks in their assistant messages; this scrub runs on load so the
// blocks never reach the rendered chat bubble. Idempotent: messages
// without blocks pass through unchanged.
const _SAFETY_BLOCK_RE = /\s*<safety>[\s\S]*?<\/safety>\s*/g;

function _scrubSafetyBlocks(messages) {
  if (!Array.isArray(messages)) return [];
  return messages.map((m) => {
    if (!m || typeof m.content !== 'string' || !m.content.includes('<safety>')) return m;
    return { ...m, content: m.content.replace(_SAFETY_BLOCK_RE, '\n\n').replace(/\n{3,}/g, '\n\n').trim() };
  });
}

/**
 * Returns the messages for an advocate session, fetched from the cloud.
 *
 * @param {string} userId
 * @param {string} sessionId
 * @returns {Promise<Array<{ role: 'user' | 'assistant', content: string, timestamp: string, messageId?: string }>>}
 */
export async function loadAdvocateMessages(userId, sessionId) {
  if (!userId || !sessionId) return [];
  try {
    const response = await apiService.getSessionDetails(sessionId);
    const raw = response?.messages || [];
    return _scrubSafetyBlocks(raw);
  } catch (_) {
    return [];
  }
}

/**
 * Returns the sessionId of the most-recent advocate session that has at
 * least one saved message, or null. Used by both the widget and full page
 * to auto-resume an active thread.
 *
 * @param {string} userId
 * @returns {Promise<string | null>}
 */
export async function getMostRecentAdvocateSession(userId) {
  const sessions = await loadAdvocateSessions(userId);
  if (sessions.length === 0) return null;
  const sorted = [...sessions].sort((a, b) => {
    const ta = a?.lastActivity ? Date.parse(a.lastActivity) : 0;
    const tb = b?.lastActivity ? Date.parse(b.lastActivity) : 0;
    return tb - ta;
  });
  for (const s of sorted) {
    if (!s?.sessionId) continue;
    if (s.messageCount > 0) return s.sessionId;
  }
  return null;
}

/**
 * Delete an advocate session and its messages. Cloud-side cascade.
 * Idempotent - returns true if the request succeeded, false on failure.
 *
 * @param {string} userId
 * @param {string} sessionId
 * @returns {Promise<boolean>}
 */
export async function deleteAdvocateSession(userId, sessionId) {
  if (!userId || !sessionId) return false;
  try {
    await apiService.deleteSession(sessionId);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('advocate-sessions-updated'));
    }
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * True when a sessionId belongs to an Advocate-source conversation.
 * Pre-999.102 this checked localStorage; now it queries the cloud list.
 *
 * @param {string} userId
 * @param {string} sessionId
 * @returns {Promise<boolean>}
 */
export async function isAdvocateSession(userId, sessionId) {
  if (!userId || !sessionId) return false;
  try {
    const sessions = await loadAdvocateSessions(userId);
    return sessions.some((s) => s.sessionId === sessionId);
  } catch (_) {
    return false;
  }
}

/**
 * Persist advocate session metadata + messages to the cloud.
 * Idempotent on sessionId. The metadata upsert sets source='advocate';
 * messages are appended in one batch.
 *
 * UX-B0: pass a title only when the conversation's title is not on file yet
 * (its first save). Pass null afterwards and the upsert leaves the stored
 * title alone, so a rename made in the sidebar survives the next turn.
 * Re-sending the title every turn wrote the derived title back over renames.
 * Needs session_manager_v5 with the UX-B0 partial update: the older
 * whole-item put replaced a missing title with 'New Conversation'.
 *
 * Pre-999.102 callers passed the FULL message history. We now expect callers
 * to pass only the NEW messages from this turn so we don't re-save history
 * each turn. AdvocateChatPage will be updated to pass the just-emitted
 * user + assistant pair.
 *
 * @param {string} userId
 * @param {string} sessionId
 * @param {string|null} title  the title for a first save, or null to keep the stored one
 * @param {Array<{ role: string, content: string, timestamp?: string, messageId?: string }>} messages
 *        New messages to append (NOT the full history)
 * @returns {Promise<boolean>} true when the save went through
 */
export async function saveAdvocateSession(userId, sessionId, title, messages) {
  if (!userId || userId === 'anonymous' || !sessionId) return false;
  try {
    const payload = { sessionId, source: 'advocate' };
    if (title) payload.title = title;
    await apiService.createOrUpdateSession(payload);
    if (Array.isArray(messages) && messages.length > 0) {
      await apiService.appendSessionMessages(
        sessionId,
        messages.map((m) => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
          messageId: m.messageId,
        })),
      );
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('advocate-sessions-updated'));
    }
    return true;
  } catch (e) {
    if (typeof window !== 'undefined' && window.console) {
      window.console.warn('saveAdvocateSession failed:', e?.message || e);
    }
    return false;
  }
}

// ---------------------------------------------------------------------------
// One-time migration helper
// ---------------------------------------------------------------------------

/**
 * Lifts legacy localStorage advocate sessions + messages to the cloud, then
 * clears the localStorage keys. Idempotent - sets a marker so it won't re-run
 * on the same browser. Safe to call on every AdvocateChatPage mount.
 *
 * Per-session errors are non-fatal; the helper attempts every legacy session
 * before clearing the marker.
 *
 * D-06 (CONTEXT 999.102): self-healing for existing veteran data.
 *
 * @param {string} userId
 * @returns {Promise<{ migrated: number, attempted: number, failures: string[], skipped: boolean }>}
 */
export async function migrateLegacyAdvocateSessions(userId) {
  if (!userId || userId === 'anonymous') {
    return { migrated: 0, attempted: 0, failures: [], skipped: true };
  }

  try {
    if (window.localStorage.getItem(MIGRATION_DONE_KEY(userId))) {
      return { migrated: 0, attempted: 0, failures: [], skipped: true };
    }
  } catch (_) {
    return { migrated: 0, attempted: 0, failures: [], skipped: true };
  }

  let raw = null;
  try {
    raw = window.localStorage.getItem(ADVOCATE_SESSIONS_KEY(userId));
  } catch (_) { /* ignore */ }

  if (!raw) {
    try {
      window.localStorage.setItem(MIGRATION_DONE_KEY(userId), '1');
    } catch (_) { /* ignore */ }
    return { migrated: 0, attempted: 0, failures: [], skipped: false };
  }

  let legacy = [];
  try {
    legacy = JSON.parse(raw) || [];
  } catch (_) {
    legacy = [];
  }

  let migrated = 0;
  const failures = [];

  for (const session of legacy) {
    if (!session?.sessionId) continue;
    try {
      await apiService.createOrUpdateSession({
        sessionId: session.sessionId,
        title: session.title || 'Conversation',
        source: 'advocate',
      });

      let msgsRaw = null;
      try {
        msgsRaw = window.localStorage.getItem(
          ADVOCATE_MESSAGES_KEY(userId, session.sessionId),
        );
      } catch (_) { /* ignore */ }

      if (msgsRaw) {
        let messages = [];
        try {
          messages = JSON.parse(msgsRaw) || [];
        } catch (_) { messages = []; }
        const valid = messages
          .filter((m) => m && (m.role === 'user' || m.role === 'assistant'))
          .filter((m) => typeof m.content === 'string')
          .map((m) => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp,
            messageId: m.messageId,
          }));
        if (valid.length > 0) {
          await apiService.appendSessionMessages(session.sessionId, valid);
        }
        try {
          window.localStorage.removeItem(
            ADVOCATE_MESSAGES_KEY(userId, session.sessionId),
          );
        } catch (_) { /* ignore */ }
      }

      migrated += 1;
    } catch (e) {
      failures.push(session.sessionId);
      if (typeof window !== 'undefined' && window.console) {
        window.console.warn(
          'migrateLegacyAdvocateSessions: session',
          session.sessionId,
          'failed:',
          e?.message || e,
        );
      }
    }
  }

  try {
    window.localStorage.removeItem(ADVOCATE_SESSIONS_KEY(userId));
    window.localStorage.setItem(MIGRATION_DONE_KEY(userId), '1');
  } catch (_) { /* ignore */ }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('advocate-sessions-updated'));
  }

  return {
    migrated,
    attempted: legacy.length,
    failures,
    skipped: false,
  };
}
